import { logger } from '@/core/logger';
import { Platform } from 'react-native';

// Simple AES-GCM crypto wrapper with best-effort WebCrypto usage.
// On platforms without SubtleCrypto, we fallback to no-op (plaintext) with warnings.
// Versioned payload format: v1:<base64(iv)>:<base64(cipher)>

const VERSION_PREFIX = 'v1:';

async function getKey(): Promise<CryptoKey | null> {
    try {
        // Derive a per-installation key from a static string using SHA-256 to get exactly 32 bytes (256 bits)
        // In production, store a random 32-byte key in SecureStore and import it.
        const subtle = (globalThis as any).crypto?.subtle;
        if (!subtle) return null;
        
        // Hash the string to get exactly 32 bytes (256 bits) for AES-256-GCM
        const material = new TextEncoder().encode('BloomRN-EncryptedStorage-Key');
        const hashBuffer = await subtle.digest('SHA-256', material);
        
        // Import the 32-byte hash as an AES-GCM key
        const key = await subtle.importKey(
            'raw',
            hashBuffer,
            { name: 'AES-GCM' },
            false,
            ['encrypt', 'decrypt']
        );
        return key;
    } catch (e) {
        logger.warn('crypto:failed-to-import-key', { error: String(e) });
        return null;
    }
}

function toB64(input: ArrayBuffer | Uint8Array): string {
    const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
    let bin = '';
    for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]!);
    return typeof btoa !== 'undefined' ? btoa(bin) : Buffer.from(bin, 'binary').toString('base64');
}

function fromB64(b64: string): Uint8Array {
    const bin = typeof atob !== 'undefined' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

export async function encrypt(plaintext: string): Promise<string> {
    try {
        const subtle = (globalThis as any).crypto?.subtle;
        const key = await getKey();
        if (!subtle || !key) {
            logger.warn('crypto:subtle-missing-plaintext-fallback', { platform: Platform.OS });
            return `${VERSION_PREFIX}${toB64(new TextEncoder().encode('plain'))}:${toB64(new TextEncoder().encode(plaintext))}`;
        }
        const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
        const enc = await subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            new TextEncoder().encode(plaintext)
        );
        return `${VERSION_PREFIX}${toB64(iv)}:${toB64(enc)}`;
    } catch (e) {
        logger.error('crypto:encrypt-failed', { error: String(e) });
        // Last resort fallback (still versioned)
        return `${VERSION_PREFIX}${toB64(new TextEncoder().encode('plain'))}:${toB64(new TextEncoder().encode(plaintext))}`;
    }
}

export async function decrypt(payload: string): Promise<string> {
    try {
        if (!payload.startsWith(VERSION_PREFIX)) {
            // legacy/plain
            return payload;
        }
        const rest = payload.slice(VERSION_PREFIX.length);
        const parts = rest.split(':');
        if (parts.length < 2) return '';
        const ivB64 = parts[0] ?? '';
        const dataB64 = parts[1] ?? '';
        const subtle = (globalThis as any).crypto?.subtle;
        const key = await getKey();
        if (!subtle || !key) {
            logger.warn('crypto:subtle-missing-plaintext-decrypt', { platform: Platform.OS });
            // Treat as base64-encoded plaintext
            const plain = new TextDecoder().decode(fromB64(dataB64));
            return plain;
        }
        const iv = fromB64(ivB64);
        const data = fromB64(dataB64);
        const dec = await subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
        return new TextDecoder().decode(dec);
    } catch (e) {
        logger.error('crypto:decrypt-failed', { error: String(e) });
        // Best-effort: try to interpret as base64/plain
        try {
            const parts = payload.startsWith(VERSION_PREFIX)
                ? payload.slice(VERSION_PREFIX.length).split(':')
                : [];
            const b64 = parts.length === 2 ? (parts[1] ?? '') : payload;
            return new TextDecoder().decode(fromB64(b64));
        } catch {
            return '';
        }
    }
}

export const CryptoWrapper = { encrypt, decrypt };


